import { BellIcon, BellOffIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { fetchPushKey } from '@/lib/api/push-subscriptions.js';
import { notify } from '@/lib/notify.js';

import {
  loadPushState,
  PUSH_STATE_CHANGED,
  pushAvailableHere,
  readPushState,
  turnPushOff,
  turnPushOn,
  whenWorkerReady,
  WorkerNotReadyError,
  type PushState,
} from './push-subscription.js';

/** What a click says while the service worker still installs; on a phone that can take minutes. */
export const GETTING_READY =
  'Notifications are getting ready. Try again in a minute.';

/** The bell's name and tooltip for each state. */
const LABEL: Record<PushState, { name: string; tip: string }> = {
  off: {
    name: 'Turn on reply notifications',
    tip: 'Notify me when a reply is ready',
  },
  on: {
    name: 'Turn off reply notifications',
    tip: 'Reply notifications are on',
  },
  blocked: {
    name: 'Reply notifications are blocked in this browser',
    tip: 'Notifications are blocked in this browser',
  },
  pending: {
    name: 'Reply notifications are getting ready',
    tip: 'Getting ready, try again in a minute',
  },
};

/**
 * Loads, once, whether the bell applies here and its state: nothing where push is unavailable or
 * off on the server. The bell shows only once the state is loaded, so a click never races the
 * load-time resync; while the service worker installs, that state is pending.
 */
function usePushSetup(): {
  publicKey: string | null;
  state: PushState;
  setState: (state: PushState) => void;
} {
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [state, setState] = useState<PushState>('off');
  useEffect(() => {
    if (!pushAvailableHere()) return;
    let live = true;
    void (async () => {
      const key = await fetchPushKey();
      if (!live || !key.enabled || !key.publicKey) return;
      const current = await loadPushState(key.publicKey);
      if (!live) return;
      setPublicKey(key.publicKey);
      setState(current);
    })();
    return () => {
      live = false;
    };
  }, []);
  useStateRefresh(publicKey, state, setState);
  return { publicKey, state, setState };
}

/** Loads the state again once a pending service worker is active, and whenever the reply prompt changed it. */
function useStateRefresh(
  publicKey: string | null,
  state: PushState,
  setState: (state: PushState) => void,
): void {
  useEffect(() => {
    if (!publicKey) return;
    let live = true;
    const reload = () =>
      void loadPushState(publicKey)
        .then((next) => live && setState(next))
        .catch(() => undefined);
    window.addEventListener(PUSH_STATE_CHANGED, reload);
    if (state === 'pending') void whenWorkerReady().then(reload);
    return () => {
      live = false;
      window.removeEventListener(PUSH_STATE_CHANGED, reload);
    };
  }, [publicKey, state, setState]);
}

/**
 * Turns notifications on or off, or explains a blocked browser or a service worker still
 * installing; says so when it fails.
 */
async function toggle(state: PushState, publicKey: string): Promise<PushState> {
  if (state === 'pending') {
    notify.info(GETTING_READY);
    return state;
  }
  if (state === 'blocked') {
    notify.info(
      'Notifications are blocked for this site. Allow them in the browser settings, then try again.',
    );
    return state;
  }
  try {
    if (state === 'on') {
      await turnPushOff();
      return 'off';
    }
    return await turnPushOn(publicKey);
  } catch (error) {
    if (error instanceof WorkerNotReadyError) {
      notify.info(GETTING_READY);
      return 'pending';
    }
    notify.error(
      state === 'on'
        ? 'Could not turn off notifications. Check the connection and try again.'
        : 'Could not turn on notifications. Check the connection and try again.',
    );
    return state;
  }
}

/**
 * The header bell for reply notifications (MEDA-1208): web push when an agent reply is ready. It is
 * absent in Teams, where the Teams bell already notifies, and wherever this browser cannot get push.
 */
export function NotificationsToggle() {
  const { publicKey, state, setState } = usePushSetup();
  const [busy, setBusy] = useState(false);
  if (!publicKey) return null;
  const Icon = state === 'on' ? BellIcon : BellOffIcon;
  const onClick = async () => {
    setBusy(true);
    try {
      const current =
        state === 'blocked'
          ? await readPushState().catch((): PushState => 'pending')
          : state;
      setState(await toggle(current, publicKey));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={LABEL[state].name}
          aria-pressed={state === 'on'}
          disabled={busy}
          onClick={() => void onClick()}
          className="size-10 rounded-full text-foreground/80 transition-colors hover:bg-foreground/[0.06] hover:text-foreground max-md:size-11"
        >
          <Icon className="size-[var(--icon-lg)] max-md:size-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{LABEL[state].tip}</TooltipContent>
    </Tooltip>
  );
}
